import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, CalendarX2 } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AllEvents from '@/components/home/AllEvents';
import { EmptyState, Section, ScrollRow, Shimmer } from '@/components/ui';
import { CategoryIcon } from '@/lib/categoryIcons';
import { subcategoryIcon } from '@/lib/subcategoryIcons';
import { useCityEvents, useSubcategories } from '@/hooks/useCatalog';
import { useEvents } from '@/context/EventContext';
import { isUpcoming } from '@/lib/filters';
import NotFound from '@/pages/NotFound';

/**
 * One sub-category's landing page — the destination behind every explore tile.
 *
 * The list is filtered client-side from the single `/services` fetch the rest
 * of the site already shares, so opening a tile costs no extra request and the
 * back button lands on a warm home page.
 */
const SubcategoryPage = () => {
  const { slug } = useParams();
  const { searchQuery } = useEvents();
  const { events, loading: eventsLoading } = useCityEvents();
  const { subcategories, loading: subsLoading } = useSubcategories();

  const subcategory = useMemo(
    () => subcategories.find((s) => s.slug === slug),
    [subcategories, slug]
  );

  const matching = useMemo(
    () =>
      events.filter((event) =>
        (event.subcategories || []).some((sub) => sub.slug === slug)
      ),
    [events, slug]
  );

  const upcomingCount = useMemo(
    () => matching.filter(isUpcoming).length,
    [matching]
  );

  const siblings = useMemo(
    () => subcategories.filter((s) => s.slug !== slug && (s.eventCount > 0 || s.isFeatured)),
    [subcategories, slug]
  );

  const loading = eventsLoading || subsLoading;

  // Only a settled lookup can conclude the slug is wrong — mid-load the list is
  // simply empty, which is not the same thing.
  if (!subsLoading && !subcategory) return <NotFound />;

  return (
    <div className="flex min-h-screen flex-col bg-surface-muted">
      <Header />
      <main className="flex-grow">
        {/* Hero */}
        <section className="relative overflow-hidden border-b border-ink-200/70 bg-white">
          {subcategory?.image && (
            <>
              <img
                src={subcategory.image}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/60 to-black/40" />
            </>
          )}

          <div
            className={`lk-container relative py-10 md:py-14 ${
              subcategory?.image ? 'text-white' : ''
            }`}
          >
            <Link
              to="/"
              className={`lk-focus mb-4 inline-flex min-h-[44px] items-center gap-1.5 rounded-lg text-sm font-medium transition-opacity hover:opacity-80 ${
                subcategory?.image ? 'text-white/90' : 'text-ink-500'
              }`}
            >
              <ArrowLeft className="h-4 w-4" />
              All events
            </Link>

            {subsLoading ? (
              <div className="space-y-3">
                <Shimmer className="h-10 w-64" />
                <Shimmer className="h-4 w-80" />
              </div>
            ) : (
              <div className="flex items-start gap-5">
                {!subcategory?.image && (
                  <div
                    className="hidden h-20 w-20 flex-none items-center justify-center rounded-2xl border border-ink-200/70 sm:flex"
                    style={{
                      background: `linear-gradient(180deg, #ffffff 0%, ${
                        subcategory?.backgroundColor || '#f2f4fb'
                      } 190%)`,
                    }}
                  >
                    <CategoryIcon
                      src={subcategoryIcon(subcategory)}
                      name={subcategory?.name}
                      className="h-14 w-14"
                    />
                  </div>
                )}

                <div className="min-w-0">
                  <h1
                    className={`text-h1 ${
                      subcategory?.image ? 'text-white drop-shadow' : 'text-ink-900'
                    }`}
                  >
                    {subcategory?.name}
                  </h1>
                  {subcategory?.tagline && (
                    <p
                      className={`mt-2 max-w-2xl text-sm md:text-base ${
                        subcategory?.image ? 'text-white/85' : 'text-ink-500'
                      }`}
                    >
                      {subcategory.tagline}
                    </p>
                  )}
                  <p
                    className={`mt-3 text-sm font-medium ${
                      subcategory?.image ? 'text-white/80' : 'text-ink-500'
                    }`}
                  >
                    {loading
                      ? 'Loading…'
                      : upcomingCount === 0
                      ? 'Nothing on right now'
                      : `${upcomingCount} upcoming ${
                          upcomingCount === 1 ? 'event' : 'events'
                        }`}
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>

        {subcategory?.description && (
          <div className="lk-container pt-8">
            <p className="max-w-3xl text-sm leading-relaxed text-ink-700">
              {subcategory.description}
            </p>
          </div>
        )}

        {/* The same filter/sort/grid the home page uses. */}
        {!loading && matching.length === 0 ? (
          <Section title="Upcoming" className="bg-white">
            <EmptyState
              icon={CalendarX2}
              title="Nothing on here just yet"
              message={`We're lining up ${
                subcategory?.name?.toLowerCase() ?? 'these'
              } events. Browse everything else in the meantime — new dates go live every week.`}
              action={
                <Link to="/" className="lk-btn-primary">
                  Browse all events
                </Link>
              }
            />
          </Section>
        ) : (
          <AllEvents
            events={matching}
            loading={loading}
            query={searchQuery}
            id="subcategory-events"
            // The page's h1 already names the bucket — "Music" then "Music
            // events" read as a stutter.
            title="Upcoming"
            emptyTitle="No events match those filters"
            emptyMessage={`Try clearing a filter — there are ${upcomingCount} ${
              subcategory?.name?.toLowerCase() ?? ''
            } events coming up.`}
          />
        )}

        {/* Never a dead end: somewhere else to go from the bottom of the page. */}
        {siblings.length > 0 && (
          <Section title="Browse something else" className="bg-surface-muted">
            <ScrollRow itemClassName="w-40">
              {siblings.map((sibling) => (
                <Link
                  key={sibling.id}
                  to={`/events/${sibling.slug}`}
                  className="lk-focus flex h-32 w-40 flex-col items-center justify-center gap-2 rounded-2xl border border-ink-200/70 bg-white p-3 text-center shadow-card transition-all duration-200 hover:-translate-y-1 hover:shadow-cardHover active:scale-95"
                  style={{
                    background: `linear-gradient(180deg, #ffffff 0%, ${
                      sibling.backgroundColor || '#f2f4fb'
                    } 190%)`,
                  }}
                >
                  <CategoryIcon
                    src={subcategoryIcon(sibling)}
                    name={sibling.name}
                    className="h-12 w-12"
                  />
                  <span className="text-xs font-bold uppercase tracking-wide text-ink-900">
                    {sibling.name}
                  </span>
                </Link>
              ))}
            </ScrollRow>
          </Section>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default SubcategoryPage;
